import type { DesignerDocument, EditCommand } from './types.js';
import { createDocument } from './types.js';

type Listener = () => void;

/**
 * Manages undo/redo history for DesignerDocument mutations.
 *
 * Designed for use with React's useSyncExternalStore:
 *   useSyncExternalStore(stack.subscribe, stack.getSnapshot)
 *
 * Snapshot identity is stable — getSnapshot() returns the same reference
 * if no mutations have occurred since the last call.
 */
export class CommandStack {
  private undoStack: EditCommand[] = [];
  private redoStack: EditCommand[] = [];
  private document: DesignerDocument;
  private listeners = new Set<Listener>();

  constructor(initialDocument?: DesignerDocument) {
    this.document = initialDocument ?? createDocument();
  }

  /** Execute a command, pushing it onto the undo stack. Clears redo stack. */
  execute(cmd: EditCommand): void {
    this.document = cmd.apply(this.document);
    this.undoStack.push(cmd);
    this.redoStack = [];
    this.notify();
  }

  /** Undo the last command. No-op if undo stack is empty. */
  undo(): void {
    const cmd = this.undoStack.pop();
    if (!cmd) return;
    this.document = cmd.revert(this.document);
    this.redoStack.push(cmd);
    this.notify();
  }

  /** Redo the last undone command. No-op if redo stack is empty. */
  redo(): void {
    const cmd = this.redoStack.pop();
    if (!cmd) return;
    this.document = cmd.apply(this.document);
    this.undoStack.push(cmd);
    this.notify();
  }

  /** Returns true if there are commands to undo. */
  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  /** Returns true if there are commands to redo. */
  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /** Number of commands on the undo stack. */
  get undoSize(): number {
    return this.undoStack.length;
  }

  /** Number of commands on the redo stack. */
  get redoSize(): number {
    return this.redoStack.length;
  }

  /** Returns the current document snapshot (stable identity). */
  getSnapshot = (): DesignerDocument => {
    return this.document;
  };

  /**
   * Subscribe to document changes. Returns an unsubscribe function.
   * Compatible with useSyncExternalStore.
   */
  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** Returns the current document. */
  getDocument(): DesignerDocument {
    return this.document;
  }

  /** Reset to initial state, clearing all history. */
  reset(doc?: DesignerDocument): void {
    this.document = doc ?? createDocument();
    this.undoStack = [];
    this.redoStack = [];
    this.notify();
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}
